import { FEATURES_CONFIG } from "../featuresConfig";

/**
 * Free-spins round tracing.
 *
 * Every place that can REFUSE to spin logs the reason here, so a round that
 * stops on its own says which gate stopped it instead of leaving us to guess.
 * The round has many independent gates — the thunk's condition, the command
 * guard, the loop, handleFreeSpin, and the win controller's main effect — and
 * any one of them failing silently looks identical from the outside: nothing
 * happens.
 *
 * Off by one flag: FEATURES_CONFIG.DEV_TOOLS.FS_DIAGNOSTICS. Not a scattering
 * of console.logs — the last set of those shipped.
 */
/**
 * One value, short enough to sit on a shared line.
 *
 * Arrays become their length, nested objects become `{…}`, long strings are
 * clipped. The point is a line you can SCAN — anything that needs unfolding
 * belongs in a breakpoint, not in a trace of eighty spins.
 */
const short = (value) => {
  if (value === null) return "null";
  if (value === undefined) return "-";
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") return "{…}";
  if (typeof value === "string") {
    return value.length > 24 ? `${value.slice(0, 24)}…` : value;
  }
  return String(value);
};

/** `a=1 b=null c=[3]` — the whole payload, flattened one level. */
const flatten = (data) => {
  if (data === null || typeof data !== "object") return short(data);
  return Object.entries(data)
    .map(([key, value]) => `${key}=${short(value)}`)
    .join(" ");
};

export const fsDiag = (tag, data) => {
  if (!FEATURES_CONFIG.DEV_TOOLS?.FS_DIAGNOSTICS) return;
  if (data === undefined) {
    console.log(`[FS] ${tag}`);
  } else {
    // ONE string, never an object: a console object is a fold-out the reader
    // has to click, and eighty of them is not a trace anyone reads.
    console.log(`[FS] ${tag} — ${flatten(data)}`);
  }
};

/**
 * Log the FIRST failing test in a list and return its name, or null when all
 * of them pass. Keeps a gate chain readable: one call, one line of output, and
 * the caller still gets a boolean.
 *
 * @param {string} where  the gate chain being evaluated
 * @param {Array<[string, boolean]>} tests  [name, blocked?] in evaluation order
 * @returns {string|null} the name of the blocking test
 */
export const fsBlockedBy = (where, tests) => {
  for (const [name, blocked] of tests) {
    if (blocked) {
      fsDiag(`${where} BLOCKED by ${name}`);
      return name;
    }
  }
  return null;
};

/**
 * The same tracing, for the BASE game.
 *
 * Free spins has had `[FS]` since it was built; the base game had nothing, so
 * an autoplay round that stopped on its own — or a trigger that never handed
 * over — looked identical from the outside to a game that was simply idle.
 * Same flag, same one-line format, different tag so the two are separable.
 */
export const baseDiag = (tag, data) => {
  if (!FEATURES_CONFIG.DEV_TOOLS?.FS_DIAGNOSTICS) return;
  if (data === undefined) {
    console.log(`[BASE] ${tag}`);
  } else {
    console.log(`[BASE] ${tag} — ${flatten(data)}`);
  }
};

/** baseDiag's twin of fsBlockedBy. */
export const baseBlockedBy = (where, tests) => {
  for (const [name, blocked] of tests) {
    if (blocked) {
      baseDiag(`${where} BLOCKED by ${name}`);
      return name;
    }
  }
  return null;
};

export default fsDiag;
